// Legal texts for Spectraloom · bilingual, keyed by document/section.
// Keep in sync with LEGAL_VERSION in backend/server.py. Bumping LEGAL_VERSION
// forces every user to re-accept.

export const LEGAL_VERSION = "1.5";
export const LEGAL_UPDATED = "2026-10-23";
export const CONTACT_EMAIL = "info@hitbeatrecords.com";

const TERMS_IT = `# Termini di Servizio

**Ultimo aggiornamento:** ${new Date(LEGAL_UPDATED).toLocaleDateString("it-IT")} · versione ${LEGAL_VERSION}

I presenti Termini e Condizioni regolano l'accesso e l'utilizzo dell'applicazione Spectraloom, fornita e gestita da Hit Beat Records di Sergio Mari (P.IVA 09654820019) con sede in Via E. Brusa 50, 10149 Torino. Nota: le transazioni finanziarie e gli acquisti sono gestiti dal nostro rivenditore autorizzato, Paddle.

## 1. Oggetto del servizio
Spectraloom ("il Servizio", "noi") è uno strumento di analisi tecnica audio online che permette agli utenti di caricare tracce musicali (WAV, MP3, AIFF, FLAC), riceverne una valutazione DSP automatizzata, un feedback tecnico generato da intelligenza artificiale e una versione ottimizzata scaricabile.

## 2. Accettazione e requisiti
Utilizzando il Servizio dichiari di avere almeno 16 anni e di accettare integralmente questi Termini. Se agisci per conto di un'azienda, dichiari di avere il potere di vincolarla.

## 3. Diritti d'autore e responsabilità dell'utente
- **Confermi di essere titolare** dei diritti d'autore (o di avere licenza valida) su ogni traccia caricata.
- Non caricherai materiale che violi diritti di terzi (copyright, marchi, contratti discografici, sample non autorizzati).
- L'analisi ha finalità puramente tecniche: Spectraloom **non concede alcun diritto sui contenuti caricati** e non li ridistribuisce.
- **Sei l'unico responsabile** del contenuto caricato. In caso di reclami DMCA o violazioni, potremo rimuovere immediatamente i contenuti e sospendere l'accesso.

## 4. Limiti d'uso
- **Free Trial (una tantum, non rinnovabile):** al momento della prima registrazione ricevi **1 analisi mastering completa**, **2 preview da 30 secondi** e **2 domande al fonico AI** incluse, valide solo per la prima analisi. Non richiediamo carta di credito. Il Free Trial non si rinnova né si ripete.
- Ulteriori crediti possono essere richiesti all'amministratore (info@hitbeatrecords.com) o acquistati tramite i piani a pagamento quando disponibili.
- Max **2 preview** di mastering per analisi.
- Max **1 file di confronto A/B** per analisi.
- Dimensione massima file caricato: **60 MB**.
- L'uso automatizzato, il web scraping o l'accesso tramite bot sono vietati.

## 4-bis. Piani di abbonamento, rinnovi e crediti
Spectraloom offre **4 livelli di accesso**. Prezzi IVA inclusa, pagamenti gestiti dal rivenditore autorizzato Paddle.

| Piano | Prezzo | Cadenza | Crediti/mese | Preview MP3 | Chat AI | Rollover crediti | Post-cancellazione |
|---|---|---|---|---|---|---|---|
| Free | €0 | una tantum | 1 totale (no master) | 2 | 2 msg | · | · |
| Singolo | €9,90 | una tantum | 1 | 3 | 5 msg | · | · |
| Basic | €23,90 | mensile | 8/mese | 24/mese | 50 msg/mese | fino a 24 crediti · 60 giorni | 60 giorni |
| Pro | €48,90 | mensile | 20/mese | 60/mese | 120 msg/mese | fino a 60 crediti · 60 giorni | 60 giorni |

**Modello crediti (1:1):** 1 credito = 1 analisi completa + 1 mastering automatico + le preview MP3 incluse nel piano. Il credito viene consumato quando l'utente avvia una nuova analisi; la stessa unità copre il mastering collegato a quella analisi. Esauriti i crediti disponibili, l'account non può eseguire nuove analisi né nuovi mastering fino al rinnovo mensile (Basic/Pro) o all'acquisto di un nuovo pacchetto (Singolo).

**Regole operative:**
- **Rinnovo automatico (Basic/Pro):** l'abbonamento si rinnova in automatico ogni mese nella stessa data di sottoscrizione. L'utente può disdire in qualsiasi momento dalla pagina account; il piano resta attivo fino alla fine del periodo pagato.
- **Rollover dei crediti non utilizzati (solo Basic/Pro):** i crediti mensili non consumati vengono trasferiti al ciclo successivo fino al limite massimo di **24 crediti per Basic** e **60 crediti per Pro**. Ogni credito accumulato ha una **validità massima di 60 giorni** dalla data di maturazione; superati i 60 giorni, i crediti residui scadono automaticamente (metodo FIFO: si consuma prima il più vecchio).
- **Accesso post-cancellazione (solo Basic/Pro):** dopo la disdetta l'utente conserva per **60 giorni** l'accesso ai crediti residui già maturati, senza nuovi rinnovi né nuovi crediti mensili. Decorsi i 60 giorni, l'account torna automaticamente al piano Free.
- **Cambio piano:** l'upgrade è immediato e proporzionato (viene riconosciuto il valore residuo del piano precedente). Il downgrade entra in vigore al termine del ciclo di fatturazione corrente.
- **Diritto di recesso (D.Lgs. 206/2005):** per gli acquisti one-off (piano Singolo) e per il primo rinnovo del piano Basic/Pro l'utente consumatore ha **14 giorni** per esercitare il diritto di recesso contattando info@hitbeatrecords.com, salvo che non abbia già utilizzato i crediti inclusi (in tal caso il diritto decade come previsto dall'art. 59 lett. o del Codice del Consumo per contenuti digitali già fruiti).
- **Rimborsi:** entro 14 giorni solo per crediti non utilizzati, gestiti da Paddle. Dettagli nella nostra [Politica di Rimborso](/legal/refund).

## 5. Uso di AI e trasparenza (EU AI Act)
In conformità al Regolamento UE 2024/1689 sull'Intelligenza Artificiale ("EU AI Act", art. 50):
- Spectraloom utilizza sistemi di **Intelligenza Artificiale di uso generale** (Anthropic Claude Haiku, Google Gemini 2.5 Flash) per generare feedback tecnico sulle tracce audio caricate.
- I contenuti prodotti dalla chat "Fonico AI" e dal feedback testuale sono **generati artificialmente**. Ogni output AI è marcato in modo chiaro nella UI.
- L'analisi DSP (LUFS, dinamica, spettro, ecc.) è invece deterministica e basata su algoritmi tradizionali (librosa, scipy).
- **Il sistema non è un dispositivo medico, non prende decisioni ad alto rischio e non profila l'utente.** È classificato come AI "Limited Risk" ai sensi dell'AI Act.

Il feedback tecnico è generato da modelli AI e ha valore **puramente indicativo**. Non sostituisce il parere di un fonico o mastering engineer umano e non garantisce alcun risultato commerciale o classifica. Spectraloom non è responsabile di decisioni artistiche o commerciali prese sulla base dei suggerimenti forniti.

## 5-bis. Limitazione di responsabilità
Nella misura massima consentita dalla legge italiana, Spectraloom e Hit Beat Records sono responsabili solo per danni diretti, prevedibili e imputabili a dolo o colpa grave, fino a un massimo pari all'importo pagato dall'utente nei 12 mesi precedenti. Sono esclusi danni indiretti, mancato profitto, perdita di opportunità commerciali o di reputazione derivanti dall'uso del feedback AI.

## 5-ter. Gestione dei Pagamenti e Rivenditore Autorizzato
I prodotti digitali e le licenze software offerti su questo sito **non sono venduti direttamente dal Titolare**. La commercializzazione, la vendita, la fatturazione e la distribuzione globale dei prodotti sono interamente delegate e gestite da **Paddle**, che agisce in qualità di **Merchant of Record e Rivenditore Autorizzato** (Authorized Reseller).

Al momento dell'acquisto, la transazione avverrà direttamente tra l'utente finale e la società **Paddle Payments Ltd** (o società del gruppo Paddle applicabile in base alla nazionalità dell'acquirente). Di conseguenza, Paddle sarà l'unico soggetto responsabile per l'incasso del pagamento, l'emissione della fattura legale e l'assolvimento di tutte le imposte locali e tasse sulle vendite (incluse IVA, VAT, Sales Tax) applicabili nel Paese dell'acquirente.

Il Titolare di questo sito mantiene la piena ed esclusiva proprietà intellettuale e i diritti d'autore sul codice sorgente del software, concedendo a Paddle esclusivamente la licenza per la sua distribuzione e rivendita commerciale.

## 6. Storage e cancellazione automatica
Le tracce caricate vengono conservate su nostri server per **fino a 24 ore** al fine di permettere ottimizzazione, download e chat contestuale, poi vengono automaticamente rimosse. I risultati testuali (punteggi, feedback) restano più a lungo per permetterti di consultare la cronologia.

## 7. Sospensione o chiusura
Possiamo sospendere l'accesso a un dispositivo in caso di abuso (uploader ripetuti di contenuti sospetti, tentativi di aggirare le quote, richieste DMCA). Puoi cancellare tutti i tuoi dati in qualsiasi momento dalla pagina Cronologia → "Cancella i miei dati".

## 8. Modifiche ai Termini
Aggiorneremo questi Termini quando necessario. Ogni modifica sostanziale comporta il bump della versione (${LEGAL_VERSION} → 1.1, 1.2, ecc.) e ti verrà chiesto di riaccettarli al prossimo accesso.

## 9. Legge applicabile e foro
Questi Termini sono regolati dalla legge italiana. Per qualsiasi controversia il foro competente è quello del luogo di residenza del consumatore, se applicabile, altrimenti il foro di Torino.

## 10. Contatti
${CONTACT_EMAIL}
`;

const TERMS_EN = `# Terms of Service

**Last updated:** ${new Date(LEGAL_UPDATED).toLocaleDateString("en-US")} · version ${LEGAL_VERSION}

These Terms and Conditions govern access to and use of the Spectraloom application, provided and operated by Hit Beat Records di Sergio Mari (VAT IT09654820019), registered office at Via E. Brusa 50, 10149 Turin, Italy. Note: financial transactions and purchases are handled by our authorised reseller, Paddle.

## 1. Service scope
Spectraloom ("the Service", "we") is an online technical audio analysis tool that lets users upload music tracks (WAV, MP3, AIFF, FLAC), receive an automated DSP evaluation, AI-generated technical feedback and a downloadable optimised version.

## 2. Acceptance and requirements
By using the Service you declare you are at least 16 years old and fully accept these Terms. If you act on behalf of a company, you declare you have authority to bind it.

## 3. Copyright and user responsibility
- **You confirm you own the rights** (or hold a valid licence) on every track you upload.
- You will not upload material that infringes third-party rights (copyright, trademarks, label contracts, unlicensed samples).
- Analysis is strictly technical: Spectraloom **claims no rights** on uploaded content and does not redistribute it.
- **You are solely responsible** for the content uploaded. In case of DMCA claims or infringements, we may immediately remove the content and suspend access.

## 4. Usage limits
- **Free Trial (one-time only, non-renewable):** upon initial signup you receive **1 complete mastering analysis**, **2 30-second previews** and **2 questions to the AI engineer** included, valid only for the first analysis. No credit card required. The Free Trial does not renew nor repeat.
- Additional credits can be requested from the administrator (info@hitbeatrecords.com) or purchased through paid plans when available.
- Max **2 mastering previews** per analysis.
- Max **1 A/B comparison file** per analysis.
- Max uploaded file size: **60 MB**.
- Automated usage, web scraping or bot access are forbidden.

## 4-bis. Subscription plans, renewals and credits
Spectraloom offers **4 access tiers**. Prices VAT included, payments handled by our authorised reseller Paddle.

| Plan | Price | Cadence | Credits/month | MP3 previews | AI chat | Credit rollover | Post-cancellation |
|---|---|---|---|---|---|---|---|
| Free | €0 | one-off | 1 total (no master) | 2 | 2 msg | · | · |
| Single | €9.90 | one-off | 1 | 3 | 5 msg | · | · |
| Basic | €23.90 | monthly | 8/mo | 24/mo | 50 msg/mo | up to 24 credits · 60 days | 60 days |
| Pro | €48.90 | monthly | 20/mo | 60/mo | 120 msg/mo | up to 60 credits · 60 days | 60 days |

**Credit model (1:1):** 1 credit = 1 full analysis + 1 automatic mastering + the plan's included MP3 previews. The credit is consumed when the user starts a new analysis; the same unit covers the mastering linked to that analysis. Once credits are exhausted, the account cannot start new analyses or new masterings until the monthly renewal (Basic/Pro) or until a new one-off package is purchased (Single).

**Operational rules:**
- **Auto-renewal (Basic/Pro):** the subscription renews automatically every month on the same subscription date. The user can cancel at any time from the account page; the plan remains active until the end of the paid period.
- **Rollover of unused credits (Basic/Pro only):** unused monthly credits carry over to the next cycle up to a maximum of **24 credits for Basic** and **60 credits for Pro**. Each accumulated credit has a **maximum validity of 60 days** from the date it was earned; after 60 days remaining credits expire automatically (FIFO: oldest credit consumed first).
- **Post-cancellation access (Basic/Pro only):** after cancelling, the user keeps access to already-earned residual credits for **60 days**, with no new renewals and no new monthly credits. After the 60 days, the account automatically reverts to the Free plan.
- **Plan change:** upgrades are immediate and prorated (the residual value of the previous plan is credited). Downgrades take effect at the end of the current billing cycle.
- **Right of withdrawal (Italian Consumer Code / EU Directive 2011/83):** for one-off purchases (Single plan) and for the first renewal of the Basic/Pro plan, the consumer has **14 days** to exercise the right of withdrawal by contacting info@hitbeatrecords.com, unless the included credits have already been used (in which case the right lapses as provided by art. 59(o) of the Italian Consumer Code for digital content already consumed).
- **Refunds:** within 14 days for unused credits only, handled by Paddle. Details in our [Refund Policy](/legal/refund).

## 5. AI use and transparency (EU AI Act)
In compliance with EU Regulation 2024/1689 on Artificial Intelligence ("EU AI Act", art. 50):
- Spectraloom uses **General-Purpose AI systems** (Anthropic Claude Haiku, Google Gemini 2.5 Flash) to generate technical feedback on uploaded audio tracks.
- Content produced by the "AI Engineer" chat and by the textual feedback is **artificially generated**. Each AI output is clearly labelled in the UI.
- DSP analysis (LUFS, dynamics, spectrum, etc.) is instead deterministic and based on traditional algorithms (librosa, scipy).
- **The system is not a medical device, does not make high-risk decisions and does not profile the user.** It is classified as "Limited Risk" AI under the AI Act.

Technical feedback is generated by AI models and is **purely indicative**. It does not replace a human mixing/mastering engineer and does not guarantee any commercial result or chart position. Spectraloom is not liable for artistic or commercial decisions taken on the basis of the suggestions provided.

## 5-bis. Limitation of liability
To the maximum extent permitted by Italian law, Spectraloom and Hit Beat Records are liable only for direct, foreseeable damages attributable to wilful misconduct or gross negligence, up to a maximum equal to the amount paid by the user in the previous 12 months. Indirect damages, loss of profit, loss of business opportunities or reputation arising from use of the AI feedback are excluded.

## 5-ter. Payment Processing and Authorized Reseller
The digital products and software licenses offered on this website are **not sold directly by the Owner**. The marketing, sale, billing, and global distribution of the products are entirely delegated to and managed by **Paddle / Paddle Payments Ltd**, acting as the **Merchant of Record and Authorized Reseller**.

Upon purchase, the transaction will occur directly between the final user and Paddle. Consequently, Paddle will be solely responsible for payment processing, issuing the legal invoice, and the collection and remittance of all local sales taxes and duties (including VAT, GST, and Sales Tax) applicable in the buyer's jurisdiction.

The Owner of this website retains full and exclusive intellectual property and copyrights over the software's source code, granting Paddle only the license for its commercial distribution and resale.

## 6. Storage and auto-deletion
Uploaded tracks are stored on our servers for **up to 24 hours** to allow optimisation, download and contextual chat, then automatically removed. Text results (scores, feedback) are kept longer so you can browse your history.

## 7. Suspension or termination
We may suspend a device's access in case of abuse (repeated suspicious uploads, quota-bypass attempts, DMCA notices). You can erase all your data at any time from History → "Delete my data".

## 8. Changes to Terms
We will update these Terms when necessary. Every substantial change bumps the version (${LEGAL_VERSION} → 1.1, 1.2, etc.) and you will be asked to re-accept on the next visit.

## 9. Governing law and venue
These Terms are governed by Italian law. For disputes the competent court is the consumer's place of residence, where applicable, otherwise the court of Turin, Italy.

## 10. Contact
${CONTACT_EMAIL}
`;

const PRIVACY_IT = `# Privacy Policy

**Ultimo aggiornamento:** ${new Date(LEGAL_UPDATED).toLocaleDateString("it-IT")} · versione ${LEGAL_VERSION}

Il presente documento descrive il trattamento dei dati personali degli utenti nel contesto dell'utilizzo di **Spectraloom** ([https://spectraloom.app](https://spectraloom.app)), conformemente all'**art. 13 del Regolamento UE n. 2016/679 ("GDPR")**.

## 1. Titolare del trattamento
**Hit Beat Records di Sergio Mari**, Via Emilio Brusa 50, 10149 Torino · P.IVA 09654820019.
Contatti: ${CONTACT_EMAIL}

## 2. Dati raccolti
- **Device ID anonimo**: un UUID generato dal browser e salvato in localStorage. È l'unico identificatore collegato alle analisi. Non colleghiamo nome, email o account.
- **Tracce audio caricate**: conservate max 24 ore in storage temporaneo, poi cancellate automaticamente.
- **Metadata delle analisi**: nome file, genere, modalità, timestamp, misure DSP (LUFS, BPM, dinamica, ecc.), feedback AI generato.
- **Contatore quota**: numero di analisi effettuate, associato al device ID.
- **Consenso registrato**: timestamp, versione legale accettata, IP (solo al momento del consenso) e user-agent.
- **Cookie tecnici**: solo localStorage interno (lingua UI, device ID, admin token opzionale). **Nessun cookie di tracciamento, nessun Google Analytics, nessun pixel Meta/TikTok.**

## 3. Basi giuridiche
- **Esecuzione del servizio richiesto** (art. 6.1.b GDPR)
- **Legittimo interesse** (art. 6.1.f GDPR) per prevenire abusi
- **Consenso esplicito** (art. 6.1.a GDPR) per accettazione Termini e conferma diritti d'autore

## 4. Finalità
Fornire l'analisi audio, generare il feedback AI, permettere download, gestire quote e prevenire abusi.

## 5. Modelli AI di terze parti e trasferimenti extra-UE
In conformità all'**EU AI Act (art. 50)** il feedback testuale e la chat "Fonico AI" sono generati da sistemi AI di terze parti:
- **Anthropic (Claude Haiku)** · server US/EU. Non inviamo mai la traccia audio, solo dati numerici e testuali derivati. DPA e SCC UE attivi.
- **Google (Gemini 2.5 Flash)** · server EU/US. Stesso trattamento. DPA e SCC UE attivi.
- Nessuno dei due addestra modelli sulle tue richieste. **Le tracce audio non vengono mai inviate a terze parti.**

Per eventuali altri trasferimenti extra-UE saranno individuate garanzie adeguate (decisioni di adeguatezza, clausole contrattuali standard SCC, norme vincolanti d'impresa).

## 5-bis. Elaborazione dei pagamenti e servizi di Merchant of Record (Paddle)
Per la vendita e la distribuzione delle licenze del nostro software, utilizziamo un servizio terzo che agisce in qualità di **Merchant of Record (MoR) e Rivenditore Autorizzato**. Al momento dell'acquisto, i dati personali e i dati di pagamento (quali nome, cognome, indirizzo email, dati della carta di credito o account PayPal e indirizzo di fatturazione) vengono raccolti e trattati direttamente da **Paddle**.

- **Soggetto che tratta i dati**: Paddle Payments Ltd (o società del gruppo Paddle, con sede in Core 3, Stark Creative Quarter, 90-94 Corporation Street, Belfast, BT1 3LG, United Kingdom).
- **Finalità del trattamento**: gestione dell'ordine, elaborazione sicura del pagamento, prevenzione delle frodi, emissione della fattura legale e assolvimento degli obblighi fiscali locali (IVA/VAT).
- **Base giuridica**: il trattamento è necessario per l'esecuzione del contratto di acquisto della licenza software stipulato direttamente tra l'utente e Paddle.
- **Luogo del trattamento**: Regno Unito / Unione Europea / USA (in conformità con le clausole contrattuali standard per il trasferimento dei dati extra-UE).

I dati di pagamento dell'utente **non vengono in alcun modo memorizzati o registrati sui server del Titolare di questo sito**. Per maggiori informazioni sulle modalità di trattamento dei dati da parte del distributore, si invita a consultare la [Privacy Policy di Paddle](https://www.paddle.com/legal/privacy).

## 6. Durata di conservazione
- **Tracce audio**: 24 ore poi cancellazione automatica
- **Metadata e feedback**: fino a 12 mesi dall'ultima attività del device
- **Consenso**: 10 anni (obbligo probatorio GDPR)

## 7. Sicurezza
Storage isolato per-device, MongoDB con autenticazione, HTTPS ovunque, admin token separato e rotabile.

## 8. I tuoi diritti (GDPR artt. 15-22)
- **Accesso**: scarica tutti i tuoi dati in JSON da Cronologia → "Esporta i miei dati"
- **Cancellazione**: elimina tutto da Cronologia → "Cancella i miei dati" · irreversibile
- **Rettifica, portabilità, opposizione, limitazione**: scrivi a ${CONTACT_EMAIL} entro 30 giorni
- **Reclamo**: al Garante Privacy ([garanteprivacy.it](https://www.garanteprivacy.it))

## 9. Modifiche
Cambiamenti sostanziali comportano il bump della versione e una nuova richiesta di consenso.

## 10. Contatti
${CONTACT_EMAIL}

---

© 2026 Spectraloom · Mix & Master Analyzer · Tutti i diritti riservati. Progetto di Hit Beat Records di Sergio Mari · P.IVA: 09654820019
`;

const PRIVACY_EN = `# Privacy Policy

**Last updated:** ${new Date(LEGAL_UPDATED).toLocaleDateString("en-US")} · version ${LEGAL_VERSION}

This document describes the processing of users' personal data in connection with the use of **Spectraloom** ([https://spectraloom.app](https://spectraloom.app)), in compliance with **art. 13 of EU Regulation No. 2016/679 ("GDPR")**.

## 1. Data controller
**Hit Beat Records di Sergio Mari**, Via Emilio Brusa 50, 10149 Turin, Italy · VAT IT09654820019.
Contact: ${CONTACT_EMAIL}

## 2. Data collected
- **Anonymous device ID**: a UUID generated by the browser and stored in localStorage. It is the only identifier linked to your analyses. We do not link any name, email or account.
- **Uploaded audio tracks**: kept for max 24 hours in temporary storage, then automatically deleted.
- **Analysis metadata**: filename, genre, mode, timestamp, DSP measurements (LUFS, BPM, dynamics, etc.), generated AI feedback.
- **Quota counter**: number of analyses performed, tied to the device ID.
- **Consent record**: timestamp, accepted legal version, IP (only at consent time) and user-agent.
- **Technical cookies**: only internal localStorage (UI language, device ID, optional admin token). **No tracking cookie, no Google Analytics, no Meta/TikTok pixel.**

## 3. Legal basis
- **Performance of the requested service** (GDPR art. 6.1.b)
- **Legitimate interest** (GDPR art. 6.1.f) to prevent abuse
- **Explicit consent** (GDPR art. 6.1.a) for Terms acceptance and copyright confirmation

## 4. Purposes
Provide the audio analysis, generate AI feedback, enable download, manage quotas and prevent abuse.

## 5. Third-party AI models and non-EU transfers
In compliance with the **EU AI Act (art. 50)** the textual feedback and the "AI Engineer" chat are generated by third-party AI systems:
- **Anthropic (Claude Haiku)** · US/EU servers. We never send the audio track, only derived numeric and textual data. Active DPA and EU SCCs.
- **Google (Gemini 2.5 Flash)** · EU/US servers. Same treatment. Active DPA and EU SCCs.
- Neither trains models on your requests. **Audio tracks are never sent to third parties.**

For any other non-EU transfers, appropriate safeguards will be identified (adequacy decisions, EU Standard Contractual Clauses SCCs, Binding Corporate Rules).

## 5-bis. Payment Processing and Merchant of Record Services (Paddle)
For the sale and distribution of our software licenses, we use a third-party service acting as a **Merchant of Record (MoR) and Authorized Reseller**. Upon purchase, your personal data and payment details (such as name, email address, credit card info or PayPal account, and billing address) are collected and processed directly by **Paddle**.

- **Data Controller**: Paddle Payments Ltd (Core 3, Stark Creative Quarter, 90-94 Corporation Street, Belfast, BT1 3LG, United Kingdom).
- **Purpose of Processing**: order fulfilment, secure payment processing, fraud prevention, legal invoicing, and compliance with local tax regulations (VAT/Sales Tax).
- **Legal Basis**: processing is necessary for the performance of the purchase contract between the user and Paddle.
- **Location of Processing**: UK / EU / USA (in accordance with standard contractual clauses for data transfers outside the EEA).

Your payment details are **never stored or accessible on this website's servers**. For further information on how your data is handled, please review the [Paddle Privacy Policy](https://www.paddle.com/legal/privacy).

## 6. Retention
- **Audio tracks**: 24 hours then automatic deletion
- **Metadata and feedback**: up to 12 months from last device activity
- **Consent**: 10 years (GDPR proof obligation)

## 7. Security
Per-device isolated storage, MongoDB with authentication, HTTPS everywhere, separate rotatable admin token.

## 8. Your rights (GDPR arts. 15-22)
- **Access**: download all your data in JSON from History → "Export my data"
- **Deletion**: erase everything from History → "Delete my data" · irreversible
- **Rectification, portability, objection, restriction**: write to ${CONTACT_EMAIL}, we reply within 30 days
- **Complaint**: to the Italian Data Protection Authority ([garanteprivacy.it](https://www.garanteprivacy.it))

## 9. Changes
Substantial changes bump the version and trigger a new consent request.

## 10. Contact
${CONTACT_EMAIL}

---

© 2026 Spectraloom · Mix & Master Analyzer · All rights reserved. A project by Hit Beat Records di Sergio Mari · VAT: IT09654820019
`;

const COOKIES_IT = `# Cookie Policy

**Ultimo aggiornamento:** ${new Date(LEGAL_UPDATED).toLocaleDateString("it-IT")} · versione ${LEGAL_VERSION}

## 1. Cosa usiamo
Spectraloom non usa cookie HTTP tradizionali di tracciamento. Usiamo esclusivamente **localStorage** (memoria del tuo browser) per salvare:

| Chiave localStorage | Scopo | Durata |
|---|---|---|
| \`spectraloom_device_id\` | UUID anonimo per legare le tue analisi al browser | Fino a cancellazione manuale |
| \`spectraloom_lang\` | Lingua UI selezionata (it/en) | Fino a cancellazione manuale |
| \`spectraloom_admin_token\` | Token admin opzionale (solo se attivato tramite URL) | Fino a cancellazione manuale |
| \`spectraloom_privacy_alert_dismissed\` | Preferenza banner "cronologia privata" | Fino a cancellazione manuale |
| \`spectraloom_cookie_banner_dismissed\` | Preferenza banner cookie | Fino a cancellazione manuale |

## 2. Cookie di terze parti
**Nessuno.** Non integriamo Google Analytics, Meta Pixel, TikTok Pixel, Hotjar o simili.

## 2-bis. Servizi di pagamento di terze parti
I pagamenti sono gestiti da Paddle.com, payment processor esterno. Paddle può utilizzare cookie tecnici propri durante il processo di acquisto, soggetti alla propria privacy policy disponibile su paddle.com.

## 3. Come rimuoverli
Vai nelle impostazioni del browser → "Cancella dati del sito" per spectraloom.app. Verrà eliminata anche la cronologia (attenzione: irreversibile).

## 4. Contatti
${CONTACT_EMAIL}
`;

const COOKIES_EN = `# Cookie Policy

**Last updated:** ${new Date(LEGAL_UPDATED).toLocaleDateString("en-US")} · version ${LEGAL_VERSION}

## 1. What we use
Spectraloom does not use traditional tracking HTTP cookies. We only use **localStorage** (your browser's memory) to store:

| localStorage key | Purpose | Duration |
|---|---|---|
| \`spectraloom_device_id\` | Anonymous UUID linking your analyses to this browser | Until manual clear |
| \`spectraloom_lang\` | Selected UI language (it/en) | Until manual clear |
| \`spectraloom_admin_token\` | Optional admin token (only if activated via URL) | Until manual clear |
| \`spectraloom_privacy_alert_dismissed\` | "History is private" banner preference | Until manual clear |
| \`spectraloom_cookie_banner_dismissed\` | Cookie banner preference | Until manual clear |

## 2. Third-party cookies
**None.** We do not integrate Google Analytics, Meta Pixel, TikTok Pixel, Hotjar or similar.

## 2-bis. Third-party payment services
Payments are handled by Paddle.com, an external payment processor. Paddle may use its own technical cookies during the purchase process, subject to its own privacy policy available at paddle.com.

## 3. How to remove them
Open your browser settings → "Clear site data" for spectraloom.app. Your history will also be deleted (warning: irreversible).

## 4. Contact
${CONTACT_EMAIL}
`;

const REFUND_IT = `# Politica di Rimborso

**Ultimo aggiornamento:** ${new Date(LEGAL_UPDATED).toLocaleDateString("it-IT")}

## 1. Chi gestisce i pagamenti
Tutti gli acquisti su Spectraloom sono elaborati da **Paddle.com**, che agisce in qualità di **Merchant of Record** e rivenditore autorizzato. Paddle emette la fattura e gestisce pagamenti, imposte e rimborsi.

## 2. Rimborso entro 14 giorni per crediti non utilizzati
Puoi richiedere il **rimborso completo entro 14 giorni** dall'acquisto **solo se non hai utilizzato nessun credito** acquistato con quel pagamento. Vale per:
- il piano **Singolo** (acquisto one-off);
- ogni pagamento dei piani in abbonamento **Basic** e **Pro**.

## 3. Quando il rimborso non è previsto
Spectraloom fornisce contenuti digitali erogati immediatamente. Al momento dell'acquisto accetti l'esecuzione immediata del servizio e prendi atto che, ai sensi dell'art. 59, lett. o) del Codice del Consumo (D.Lgs. 206/2005) e dell'art. 16, lett. m) della Direttiva UE 2011/83, **il diritto di recesso decade una volta iniziata la fruizione**. Di conseguenza **non è previsto alcun rimborso** se, con il pagamento in questione, è stata effettuata **anche una sola analisi** (inclusi report PDF, file WAV ottimizzati e domande al fonico AI).

## 4. Abbonamenti Basic e Pro
- Puoi **disdire in qualsiasi momento** dal tuo account o tramite [paddle.net](https://paddle.net): non verranno effettuati ulteriori addebiti e il piano resta attivo fino alla fine del periodo già pagato.
- Il mese in corso **non è rimborsabile** se in quel periodo sono stati utilizzati crediti.

## 5. Errori tecnici
Se un'analisi non va a buon fine per un **errore del nostro sistema**, il credito viene **restituito automaticamente** sul tuo account. Se il problema persiste, scrivici: valuteremo il ripristino del credito o il rimborso del relativo importo.

## 6. Come richiedere un rimborso
- Tramite **[paddle.net](https://paddle.net)**, inserendo l'email usata per l'acquisto; oppure
- rispondendo all'email di ricevuta Paddle; oppure
- scrivendo a **${CONTACT_EMAIL}** indicando email di acquisto e numero d'ordine.

Le richieste vengono verificate in base allo storico di utilizzo dell'account. I rimborsi approvati sono riaccreditati da Paddle sul metodo di pagamento originale, di norma entro 5–10 giorni lavorativi, e i relativi crediti e piani vengono rimossi dall'account. Non sono rimborsabili gli acquisti in caso di frode, abuso o violazione dei Termini di Servizio.

## 7. Diritti del consumatore
La presente politica non limita i diritti riconosciuti dalla legge al consumatore (D.Lgs. 206/2005 e Direttiva UE 2011/83) né i [Termini per gli acquirenti di Paddle](https://www.paddle.com/legal/checkout-buyer-terms).

## 8. Contatti
Hit Beat Records di Sergio Mari · P.IVA 09654820019 · Via E. Brusa 50, 10149 Torino · ${CONTACT_EMAIL}
`;

const REFUND_EN = `# Refund Policy

**Last updated:** ${new Date(LEGAL_UPDATED).toLocaleDateString("en-US")}

## 1. Who handles payments
All purchases on Spectraloom are processed by **Paddle.com**, acting as **Merchant of Record** and authorised reseller. Paddle issues the invoice and handles payments, taxes and refunds.

## 2. 14-day refund for unused credits
You can request a **full refund within 14 days** of purchase **only if none of the credits** bought with that payment have been used. This applies to:
- the **Single** plan (one-off purchase);
- every payment of the **Basic** and **Pro** subscriptions.

## 3. When refunds are not available
Spectraloom supplies digital content delivered immediately. At checkout you consent to immediate performance and acknowledge that, under Article 16(m) of EU Directive 2011/83 (art. 59(o) of the Italian Consumer Code), **the right of withdrawal is lost once use has begun**. Therefore **no refund is available** if **even a single analysis** has been performed with that payment (including PDF reports, optimized WAV files and AI engineer questions).

## 4. Basic and Pro subscriptions
- You can **cancel at any time** from your account or via [paddle.net](https://paddle.net): no further charges will be made and the plan stays active until the end of the period already paid.
- The current month is **not refundable** if credits have been used during that period.

## 5. Technical errors
If an analysis fails because of **an error on our side**, the credit is **automatically returned** to your account. If the problem persists, contact us: we will restore the credit or refund the related amount.

## 6. How to request a refund
- Via **[paddle.net](https://paddle.net)**, using the email address used for the purchase; or
- by replying to your Paddle receipt email; or
- by writing to **${CONTACT_EMAIL}** with your purchase email and order number.

Requests are checked against the account's usage history. Approved refunds are returned by Paddle to the original payment method, usually within 5–10 business days, and the related credits and plan are removed from the account. Purchases involving fraud, abuse or violation of the Terms of Service are not refundable.

## 7. Consumer rights
This policy does not limit your statutory consumer rights (EU Directive 2011/83) or the [Paddle Buyer Terms](https://www.paddle.com/legal/checkout-buyer-terms).

## 8. Contact
Hit Beat Records di Sergio Mari · VAT IT09654820019 · Via E. Brusa 50, 10149 Turin, Italy · ${CONTACT_EMAIL}
`;

export const LEGAL_TEXTS = {
  refund: { it: REFUND_IT, en: REFUND_EN },
  terms: { it: TERMS_IT, en: TERMS_EN },
  privacy: { it: PRIVACY_IT, en: PRIVACY_EN },
  cookies: { it: COOKIES_IT, en: COOKIES_EN },
};
